/**
 * NegoToolbar — Onglets (compteurs) + recherche + filtres + tri + toggle Tableau/Kanban.
 */
import React from 'react';
import { Search, Filter, ArrowUpDown, ChevronDown, LayoutGrid, List, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem, DropdownMenuItem,
} from '../ui/dropdown-menu';

export const TABS = [
  { key: 'active', label: 'À traiter', countKey: 'active' },
  { key: 'overdue', label: 'SLA à risque', countKey: 'overdue', accent: 'bg-rose-500' },
  { key: 'escalated', label: 'Escaladées', countKey: 'escalated', accent: 'bg-purple-500' },
  { key: 'pending_client', label: 'En attente client', countKey: 'pendingClient', accent: 'bg-blue-500' },
  { key: 'pending_internal', label: 'En attente transporteur', countKey: 'pendingInternal', accent: 'bg-orange-500' },
  { key: 'mine', label: 'Mes dossiers', countKey: 'mine' },
  { key: 'all', label: 'Toutes', countKey: 'total' },
];

const Tabs = ({ activeTab, onChange, stats }) => (
  <div className="flex gap-1 overflow-x-auto border-b border-slate-200" data-testid="nego-tabs">
    {TABS.map((t) => {
      const active = activeTab === t.key;
      const count = stats?.[t.countKey] ?? 0;
      return (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          data-testid={`nego-tab-${t.key}`}
          className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition-colors ${
            active ? 'border-emerald-600 font-bold text-emerald-700'
              : 'border-transparent font-medium text-slate-500 hover:text-slate-800'
          }`}
        >
          {t.label}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
            active ? 'bg-emerald-100 text-emerald-700'
              : count > 0 && t.accent ? `${t.accent} text-white` : 'bg-slate-100 text-slate-500'
          }`}>{count}</span>
        </button>
      );
    })}
  </div>
);

const FilterButton = ({ label, options, value, onChange, testId }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" size="sm" className="h-9 border-slate-200 text-sm text-slate-700" data-testid={testId}>
        <Filter className="mr-1.5 h-3.5 w-3.5" /> {label}
        {value?.length > 0 && <span className="ml-1.5 rounded bg-emerald-100 px-1.5 text-[10px] font-bold text-emerald-700">{value.length}</span>}
        <ChevronDown className="ml-1 h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-56">
      <DropdownMenuLabel className="text-xs">Filtrer par {label.toLowerCase()}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {options.map((o) => (
        <DropdownMenuCheckboxItem
          key={o.value}
          checked={value?.includes(o.value)}
          onCheckedChange={(c) => onChange(c ? [...(value || []), o.value] : (value || []).filter((x) => x !== o.value))}
          data-testid={`${testId}-${o.value}`}
        >{o.label}</DropdownMenuCheckboxItem>
      ))}
      {value?.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onChange([])} className="text-xs"><X className="mr-2 h-3 w-3" /> Réinitialiser</DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const NegoToolbar = ({
  activeTab, onTab, stats, search, onSearch, types, typeFilters, onTypeFilters,
  statusFilters, onStatusFilters, sortBy, onSort, view, onView, statusOptions,
}) => (
  <div className="space-y-3">
    <Tabs activeTab={activeTab} onChange={onTab} stats={stats} />
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Rechercher n° op, client, commercial…"
            className="h-9 w-64 pl-8 text-sm"
            data-testid="nego-search"
          />
        </div>
        <FilterButton label="Type" options={types} value={typeFilters} onChange={onTypeFilters} testId="nego-filter-type" />
        <FilterButton label="Statut" options={statusOptions} value={statusFilters} onChange={onStatusFilters} testId="nego-filter-status" />
      </div>
      <div className="flex items-center gap-2">
        <Select value={sortBy} onValueChange={onSort}>
          <SelectTrigger className="h-9 w-48 text-sm" data-testid="nego-sort">
            <ArrowUpDown className="mr-1.5 h-3.5 w-3.5 shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="lastActivity">Tri : Dernière activité</SelectItem>
            <SelectItem value="sla">SLA le plus urgent</SelectItem>
            <SelectItem value="amountDesc">Montant le plus élevé</SelectItem>
            <SelectItem value="gapAsc">Écart le plus faible</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center rounded-md border border-slate-200 bg-white p-0.5" data-testid="nego-view-toggle">
          <button type="button" onClick={() => onView('table')} data-testid="nego-view-table"
            className={`flex h-8 w-8 items-center justify-center rounded ${view === 'table' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
            <List className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => onView('kanban')} data-testid="nego-view-kanban"
            className={`flex h-8 w-8 items-center justify-center rounded ${view === 'kanban' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default NegoToolbar;
